import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import ConfirmModal from "@/components/ui/confirm-modal";
import { Plus, Edit, Delete } from "@/components/icons";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type { PresetLocationApi } from "@/features/education-management/types/education-management.types";
import CreateLocationModal from "@/features/education-catalog/components/CreateLocationModal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./LocationsPage.module.scss";

const LocationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.LOCATIONS_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.LOCATIONS_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.LOCATIONS_DELETE);

  const [locations, setLocations] = useState<PresetLocationApi[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<PresetLocationApi | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchLocations = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await educationManagementApi.getLocations({
        page: 1,
        limit: 100,
        search: search.trim() || undefined,
      });
      setLocations(res?.data ?? []);
    } catch {
      setLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations]);

  const filtered = useMemo(() => locations, [locations]);

  const openCreate = () => {
    setEditing(null);
    setIsFormOpen(true);
  };

  const openEdit = (loc: PresetLocationApi) => {
    setEditing(loc);
    setIsFormOpen(true);
  };

  const handleSaved = (loc: PresetLocationApi) => {
    if (editing) {
      setLocations((prev) => prev.map((p) => (p.id === loc.id ? loc : p)));
    } else {
      setLocations((prev) => [loc, ...prev]);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await educationManagementApi.deleteLocation(deletingId);
      setLocations((prev) => prev.filter((p) => p.id !== deletingId));
      toast.success(t("locations.deleteSuccess"));
      setDeletingId(null);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t("locations.deleteError"),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <Header title={t("locations.title")} />

      <div className={styles.container}>
        <div className={styles.toolbar}>
          <Input
            placeholder={t("locations.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {canCreate && (
            <Button
              variant="contained"
              color="primary"
              onClick={openCreate}
              startIcon={<Plus width={16} height={16} color="#fff" />}
            >
              {t("locations.addButton")}
            </Button>
          )}
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("locations.columns.name")}</th>
                <th>{t("locations.columns.address")}</th>
                <th>{t("locations.columns.coords")}</th>
                <th className={styles.actionsCol}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className={styles.emptyCell}>
                    {t("common.loading")}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.emptyCell}>
                    {t("locations.empty")}
                  </td>
                </tr>
              ) : (
                filtered.map((loc) => (
                  <tr key={loc.id}>
                    <td>{loc.name}</td>
                    <td className={styles.addressCell}>{loc.address || "—"}</td>
                    <td className={styles.coordsCell}>
                      {loc.latitude != null && loc.longitude != null
                        ? `${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}`
                        : "—"}
                    </td>
                    <td>
                      <div className={styles.actions}>
                        {canUpdate && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            onClick={() => openEdit(loc)}
                            aria-label={t("common.edit")}
                          >
                            <Edit width={16} height={16} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                            onClick={() => setDeletingId(loc.id)}
                            aria-label={t("common.delete")}
                          >
                            <Delete width={16} height={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateLocationModal
        isOpen={isFormOpen}
        editing={editing}
        onClose={() => {
          setIsFormOpen(false);
          setEditing(null);
        }}
        onCreated={handleSaved}
      />

      <ConfirmModal
        isOpen={!!deletingId}
        onClose={() => !isDeleting && setDeletingId(null)}
        onConfirm={handleDelete}
        title={t("locations.deleteConfirm.title")}
        message={t("locations.deleteConfirm.message")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        loading={isDeleting}
        confirmVariant="danger"
      />

    </DashboardLayout>
  );
};

export default LocationsPage;
